import api from './api';
import { ApiResponse, AttendanceStatus, InformationType } from '../types';

// ---- Public shapes (kept tight — matches the new backend contract) ----
//
// Wave 5: this service now speaks the Session / Class / SectionInstance model.
// All endpoints take {sessionId, classId, sectionInstanceId?} instead of the
// legacy {batchId, classId, sectionId?}. The DB still stores legacy fields on
// historical rows, but new reads/writes flow through the new ids.

export interface StudentAttendanceRecord {
  _id?: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
  // Reserved for the future gate-machine integration. Optional today.
  firstScan?: string;
  lastScan?: string;
  scanCount?: number;
  lateByMinutes?: number;
  isManualOverride?: boolean;
  // True when the cell came from the holiday / weekly-off overlay (no real mark).
  isOverlay?: boolean;
}

export interface StudentSummary {
  _id: string;
  name: string;
  rollNumber: string;
  phone?: string | null;
  fatherName?: string | null;
  fatherPhone?: string | null;
  section?: string | null;
}

export interface ScopeRefs {
  session: { _id: string; name: string };
  class:   { _id: string; name: string };
  section: { _id: string; name: string } | null;
}

export interface DailyAttendanceData extends ScopeRefs {
  date: string;
  attendanceData: Array<{
    student: StudentSummary;
    attendance: (StudentAttendanceRecord & { _id?: string }) | null;
  }>;
}

export interface MonthlyAttendanceData extends ScopeRefs {
  year: number;
  month: number;
  dates: string[];
  studentAttendanceData: Array<{
    student: StudentSummary;
    attendance: { [date: string]: StudentAttendanceRecord };
  }>;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface AttendanceFormData {
  studentId: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

export interface BulkAttendancePayload {
  sessionId: string;
  classId: string;
  sectionInstanceId?: string;
  date: string;
  attendanceRecords?: AttendanceFormData[];
  // Students the caller wants to explicitly UN-mark for this date. The
  // backend soft-deletes (isActive=false) any existing record for each id,
  // and records the undo in overrideHistory.
  removeStudentIds?: string[];
  applyToAll?: boolean;
  status?: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

export interface BulkAttendanceResult {
  results?: { studentId: string; success: boolean; message: string }[];
  summary: { total: number; successful: number; failed: number };
  totalStudents?: number;
  processed?: number;
  date?: string;
}

class AttendanceService {
  private baseUrl = '/attendance';

  // ---- Pure utility helpers (no I/O) ----

  /** Format a Date to YYYY-MM-DD using the host's local clock. The backend
   *  normalises into the school timezone, so this stays simple here. */
  static formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  static getTodayFormatted(): string {
    return AttendanceService.formatDateForAPI(new Date());
  }

  // ---- API surface (mirrors employee attendance service) ----

  async getDailyAttendance(
    sessionId: string,
    classId: string,
    date: string,
    sectionInstanceId?: string
  ): Promise<DailyAttendanceData> {
    const params: any = { sessionId, classId, date };
    if (sectionInstanceId) params.sectionInstanceId = sectionInstanceId;
    const response = await api.get<ApiResponse<DailyAttendanceData>>(this.baseUrl, { params });
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data!;
  }

  async markBulkAttendance(payload: BulkAttendancePayload): Promise<BulkAttendanceResult> {
    const response = await api.post<ApiResponse<BulkAttendanceResult>>(
      `${this.baseUrl}/bulk`,
      payload
    );
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data!;
  }

  async markAllPresent(
    sessionId: string,
    classId: string,
    date: string,
    sectionInstanceId?: string
  ): Promise<BulkAttendanceResult> {
    const response = await api.post<ApiResponse<BulkAttendanceResult>>(
      `${this.baseUrl}/mark-all-present`,
      { sessionId, classId, sectionInstanceId, date }
    );
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data!;
  }

  async getMonthlyAttendance(
    sessionId: string,
    classId: string,
    year: number,
    month: number,
    options: {
      sectionInstanceId?: string;
      page?: number;
      limit?: number;
      search?: string;
    } = {}
  ): Promise<MonthlyAttendanceData> {
    const params: any = { sessionId, classId, year, month };
    if (options.sectionInstanceId) params.sectionInstanceId = options.sectionInstanceId;
    if (options.page) params.page = options.page;
    if (options.limit) params.limit = options.limit;
    if (options.search) params.search = options.search;
    const response = await api.get<ApiResponse<MonthlyAttendanceData>>(
      `${this.baseUrl}/monthly`,
      { params }
    );
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data!;
  }

  /** The whole class's month for the register PDF — pages through the grid at the API's maximum page size. */
  async getMonthlyAttendanceAll(
    sessionId: string,
    classId: string,
    year: number,
    month: number,
    sectionInstanceId?: string
  ): Promise<MonthlyAttendanceData> {
    const first = await this.getMonthlyAttendance(sessionId, classId, year, month, { sectionInstanceId, page: 1, limit: 100 });
    const rows = [...first.studentAttendanceData];
    for (let page = 2; page <= first.pagination.totalPages; page += 1) {
      // eslint-disable-next-line no-await-in-loop -- pages are sequential by nature
      const next = await this.getMonthlyAttendance(sessionId, classId, year, month, { sectionInstanceId, page, limit: 100 });
      rows.push(...next.studentAttendanceData);
    }
    return { ...first, studentAttendanceData: rows };
  }

  async overrideAttendance(
    attendanceId: string,
    payload: { status: AttendanceStatus; informationType?: InformationType; remarks?: string }
  ): Promise<any> {
    const response = await api.put<ApiResponse>(
      `${this.baseUrl}/${attendanceId}/override`,
      payload
    );
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data;
  }
}

export const attendanceService = new AttendanceService();
export { AttendanceService };
export default attendanceService;
